import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { CertificateGenerator } from "@/components/CertificateGenerator";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

interface Certificate {
  learner_name: string;
  blog_post_title: string;
  certificate_number: string;
  issued_at: string;
}

const VerifyCertificate = () => {
  const { certificateId } = useParams<{ certificateId: string }>();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      if (!certificateId) {
        setError("No certificate ID provided");
        setLoading(false);
        return;
      }

      try {
        const { data: rows, error } = await supabase
          .rpc("verify_certificate", { _certificate_number: certificateId });
        const data = rows && rows.length > 0 ? rows[0] : null;

        if (error) throw error;

        if (!data) {
          setError("Certificate not found");
        } else {
          setCertificate(data);
        }
      } catch (err) {
        console.error("Error fetching certificate:", err);
        setError("Failed to verify certificate");
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [certificateId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <Helmet>
        <title>Verify Certificate - RunoBlog</title>
        <meta
          name="description"
          content="Verify the authenticity of a RunoBlog learning certificate"
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Navigation />
        <main className="flex-1 container mx-auto px-4 py-12">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-8 text-foreground">
              Certificate Verification
            </h1>

            {loading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Verifying certificate...</span>
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="mb-8">
                <XCircle className="h-5 w-5" />
                <AlertDescription className="ml-2">
                  <strong>Verification Failed:</strong> {error}
                </AlertDescription>
              </Alert>
            )}

            {certificate && (
              <div className="space-y-8">
                <Alert className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <AlertDescription className="ml-2 text-green-800 dark:text-green-200">
                    <strong>Verified:</strong> This certificate is authentic and was issued by RunoBlog.
                  </AlertDescription>
                </Alert>

                <Card className="p-6 bg-card border-border">
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">Certificate Details</h2>
                  <dl className="space-y-3 text-foreground">
                    <div className="flex flex-col sm:flex-row sm:gap-4">
                      <dt className="font-medium text-muted-foreground min-w-[200px]">Certificate Number:</dt>
                      <dd className="font-mono">{certificate.certificate_number}</dd>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:gap-4">
                      <dt className="font-medium text-muted-foreground min-w-[200px]">Recipient Name:</dt>
                      <dd>{certificate.learner_name}</dd>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:gap-4">
                      <dt className="font-medium text-muted-foreground min-w-[200px]">Course Completed:</dt>
                      <dd>28-Day Learning Roadmap: {certificate.blog_post_title}</dd>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:gap-4">
                      <dt className="font-medium text-muted-foreground min-w-[200px]">Issue Date:</dt>
                      <dd>{formatDate(certificate.issued_at)}</dd>
                    </div>
                  </dl>
                </Card>

                <div className="flex justify-center">
                  <div className="scale-75 sm:scale-90 origin-top">
                    <CertificateGenerator
                      learnerName={certificate.learner_name}
                      blogPostTitle={certificate.blog_post_title}
                      completionDate={formatDate(certificate.issued_at)}
                      certificateNumber={certificate.certificate_number}
                    />
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && !certificate && (
              <Alert variant="destructive">
                <XCircle className="h-5 w-5" />
                <AlertDescription className="ml-2">
                  No certificate found with ID: {certificateId}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default VerifyCertificate;
